/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';
import _ from 'lodash';
import urlJoin from 'proper-url-join';

import { StoreQueryParams } from '../../../../ts';
import { Example, Intent } from '../../../../ts/action';
import { ReactivateSession } from '../../../../ts/session';
import { Skill } from '../../../../ts/skill';
import { getAccessPrefixedUrl } from '../../../ia/util/access-control';
import { endpoints } from '../../../ia/util/config';
import validation from '../../../ia/util/validation';
import { getFormatter, saveAs } from '../../util/download';

const intentsEndpoint = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) =>
  urlJoin(
    endpoints.store,
    getAccessPrefixedUrl(
      useNewIAUpdatedAccess,
      assistantId,
      `skills/${skillId}/workspace/intents`
    )
  );

const intentEndpoint = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  intent: any
) =>
  intentsEndpoint(skillId, useNewIAUpdatedAccess, assistantId) +
  '/' +
  encodeURIComponent(intent);

const nextIntentsURL = (next: string) => `${endpoints.store}` + next;

const addExampleRecommendation = (skillId: string, intentId: string) =>
  endpoints.store + '/v2/skills/' + skillId + '/workspace/intents/' + intentId;

const workspaceContentEndpoint = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) =>
  urlJoin(
    endpoints.store,
    getAccessPrefixedUrl(
      useNewIAUpdatedAccess,
      assistantId,
      `skills/${skillId}/workspace/content`
    )
  );

const workspaceEndpoint = (skillId: string) =>
  `${endpoints.store}/v2/skills/${skillId}`;

const filteredCollectionEndpoint = (skillId: string) =>
  endpoints.store +
  '/v2/skills/' +
  skillId +
  '/workspace/content/filtered_collections';

const exportIntentsEndpoint = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
) =>
  useNewIAUpdatedAccess // NOTE do not use getAccessPrefixedUrl: content url different format
    ? `${endpoints.content}/assistants/${assistantId}/skills/${skillId}/export/intents`
    : `${endpoints.content}/skills/${skillId}/export/intents`;

type ListIntentsParams = StoreQueryParams &
  Partial<{
    export: boolean;
    include_count: boolean;
  }>;
type IntentsAPIResponse = {
  intents: Intent[];
  pagination: {
    total: number;
    next_url?: string;
  };
};
export type IntentsListResponse = {
  intents: Intent[];
  total?: number;
  nextURL?: string;
};

const validateAll = (skillId: string, intent?: Intent, intents?: Intent[]) => {
  const validationErrors: string[] | Record<string, string[]> = [];
  const skillErrors = validation.validateGuid(skillId);
  if (skillErrors) {
    validationErrors.push(skillErrors);
  }
  if (intent) {
    const intentErrors = validation.validateIntent(intent);
    if (intentErrors) {
      validationErrors.push(intentErrors);
    }
  }
  if (intents) {
    const intentsErrors = validation.validateIntents(intents);
    if (intentsErrors) {
      validationErrors.push(intentsErrors);
    }
  }

  if (validationErrors.length) {
    return Promise.reject({ data: { error: validationErrors } });
  }
  return null;
};

export const createIntent = async (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  intent: Intent,
  clusterName = ''
): Promise<Intent> => {
  const validationErrors = validateAll(skillId, intent);
  if (validationErrors) {
    return validationErrors;
  }

  const params: Intent = {
    intent: intent.intent,
    description: intent.description,
    global: intent.global,
    examples: intent.examples
  };
  let url = intentsEndpoint(skillId, useNewIAUpdatedAccess, assistantId);
  if (url.indexOf('?') === -1) {
    url += '?include_audit=true';
  } else if (url.indexOf('include_audit') === -1) {
    url += '&include_audit=true';
  }
  let config = { headers: {} };
  if (clusterName) {
    config = {
      headers: {
        'X-Watson-Intent-Recommendation': clusterName,
        'X-Watson-Recommendation-Query-Id': 'True'
      }
    };
  }
  try {
    const response = await axios.post<Intent>(url, params, config);
    return response.data;
  } catch (error) {
    throw _.get(error, 'response.data') || error;
  }
};

export const filterIntents = async (
  skillId: string,
  params: ListIntentsParams = {},
  queryString = ''
): Promise<Intent[]> => {
  const validationErrors = validateAll(skillId);
  if (validationErrors) {
    return validationErrors;
  }
  let queryParam = '';
  if (queryString.length && queryString.charAt(0) === '#') {
    //If # is in the queryString look for an exact match with a wildcard at the end.
    queryString = queryString.slice(1); // Remove # since it isn't supported in the API
    queryParam = `?filter=intent::${queryString}*`;
  } else if (queryString.length) {
    queryParam = `?q=intent~${queryString}`;
  }
  const config = {
    params: params
  };
  const url = intentsEndpoint(skillId, false, '') + queryParam;
  try {
    const response = await axios.get<IntentsAPIResponse>(url, config);
    return response.data.intents;
  } catch (error) {
    throw error;
  }
};

export const listIntents = async (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  extraParams: ListIntentsParams = {},
  reactivateSession?: ReactivateSession
): Promise<IntentsListResponse> => {
  const validationErrors = validateAll(skillId);
  if (validationErrors) {
    return validationErrors;
  }

  const url = intentsEndpoint(skillId, useNewIAUpdatedAccess, assistantId);

  const params: ListIntentsParams = {
    export: true,
    page_limit: 100,
    include_count: true,
    include_audit: true,
    sort: 'intent',
    ...extraParams
  };

  const config = { params };
  try {
    const response = await axios.get<IntentsAPIResponse>(
      urlJoin(url, {
        query: {
          reactivateSession:
            reactivateSession === undefined ? undefined : `${reactivateSession}`
        }
      }),
      config
    );
    return {
      intents: response.data.intents,
      total: response.data.pagination.total,
      nextURL: response.data.pagination.next_url
    };
  } catch (error) {
    throw _.get(error, 'response.data') || error;
  }
};

export const listMoreIntents = async (
  nextURL: string
): Promise<IntentsListResponse> => {
  const params: ListIntentsParams = {};
  if (nextURL.indexOf('include_audit') === -1) {
    params.include_audit = true;
  }
  try {
    const response = await axios.get<IntentsAPIResponse>(
      nextIntentsURL(nextURL),
      { params }
    );
    return {
      intents: response.data.intents,
      nextURL: response.data.pagination.next_url
    };
  } catch (error) {
    throw _.get(error, 'response.data') || error;
  }
};

export const getFilteredList = async (
  skillId: string,
  intentsArray: string[]
): Promise<IntentsListResponse> => {
  const validationErrors = validateAll(skillId);
  if (validationErrors) {
    return validationErrors;
  }

  let url = filteredCollectionEndpoint(skillId);
  url +=
    '?export=true&page_limit=99999&include_count=true&include_audit=true&sort=intent';

  const valuesArray: { id: string }[] = [];
  _.forEach(intentsArray, function (intent) {
    valuesArray.push({ id: intent });
  });
  const payload = {
    resource: 'intent',
    value: valuesArray
  };

  try {
    const response = await axios.post(url, payload);
    const intents = _.get(response, 'data.intents', []);
    return {
      intents: intents,
      total: intents.length
    };
  } catch (error) {
    throw _.get(error, 'response.data') || error;
  }
};

export const deleteIntent = async (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  intent: Intent
): Promise<{}> => {
  try {
    await validateAll(skillId, intent);
    const response = await axios.delete<{}>(
      intentEndpoint(skillId, useNewIAUpdatedAccess, assistantId, intent.intent)
    );
    return response.data;
  } catch (err) {
    throw _.get(err, 'response.data') || err;
  }
};

export const removeIntents = async (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  intents: Intent[]
): Promise<{}> => {
  const validationErrors = validateAll(skillId, null, intents);
  if (validationErrors) {
    return validationErrors;
  }

  const url = workspaceContentEndpoint(
    skillId,
    useNewIAUpdatedAccess,
    assistantId
  );
  const value = intents.map(intent => ({ id: intent.intent }));

  const params = {
    operation: 'delete',
    resource: 'intent',
    value
  };

  try {
    const response = await axios.patch<{}>(url, params);
    return response.data;
  } catch (error) {
    throw _.get(error, 'response.data') || error;
  }
};

// equivalent to old bulk remove
export const deleteAllIntents = async (skillId: string): Promise<{}> => {
  const validationErrors = validateAll(skillId);
  if (validationErrors) {
    return validationErrors;
  }

  const url = workspaceEndpoint(skillId);

  const params = { workspace: { intents: [] } };

  try {
    const response = await axios.post<{}>(url, params);
    return response.data;
  } catch (error) {
    throw _.get(error, 'response.data') || error;
  }
};

// equivalent to old bulk replace
export const replaceIntents = async (
  skillId: string,
  intentsToKeep: Intent[] = []
): Promise<Skill> => {
  const validationErrors = validateAll(skillId);
  if (validationErrors) {
    return validationErrors;
  }

  const url = workspaceEndpoint(skillId);

  const params = { workspace: { intents: intentsToKeep } };

  try {
    const response = await axios.post(url, params);
    return response.data;
  } catch (error) {
    throw _.get(error, 'response.data') || error;
  }
};

export const addExamples = async (
  skillId: string,
  intent: Intent,
  examples: Example[],
  recommendsAction: any,
  headerId: string,
  clusterName = ''
): Promise<Intent> => {
  const validationErrors = validateAll(skillId, intent);
  if (validationErrors) {
    return validationErrors;
  }

  let config = {};
  let url = addExampleRecommendation(skillId, intent.intent);
  url += '?append=true&include_audit=true';

  if (recommendsAction) {
    config = {
      headers: {
        'X-Watson-Recommendation-Id': headerId
      }
    };
  } else if (!recommendsAction) {
    config = {
      headers: {
        'X-Watson-Recommendation-Query-Id': headerId
      }
    };
  }
  // Send a header to the backend to kickoff cluster processing when examples were added from a recommended intent
  if (clusterName) {
    config = {
      headers: {
        'X-Watson-Intent-Recommendation': 'EX|' + clusterName,
        'X-Watson-Recommendation-Query-Id': 'True'
      }
    };
  }

  try {
    const response = await axios.post(url, { examples }, config);
    return response.data;
  } catch (error) {
    throw _.get(error, 'response.data') || error;
  }
};

export const getIntent = async (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  intent: string
): Promise<Intent> => {
  const validationErrors = validateAll(skillId);
  if (validationErrors) {
    return validationErrors;
  }
  const url = intentEndpoint(
    skillId,
    useNewIAUpdatedAccess,
    assistantId,
    intent
  );
  const params: ListIntentsParams = {
    export: true,
    include_audit: true
  };
  const config = { params };
  try {
    const response = await axios.get<Intent>(url, config);
    return response.data;
  } catch (error) {
    throw _.get(error, 'response.data') || error;
  }
};

export const updateIntent = async (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  currentIntent: string,
  intent: Intent
): Promise<Intent> => {
  const validationErrors = validateAll(skillId, intent);
  if (validationErrors) {
    return validationErrors;
  }
  const url = intentEndpoint(
    skillId,
    useNewIAUpdatedAccess,
    assistantId,
    currentIntent
  );
  const params: Intent = {
    intent: intent.intent,
    description: intent.description,
    global: intent.global,
    examples: intent.examples
  };
  try {
    const response = await axios.post<Intent>(url, params);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const importIntents = async (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  file: File
): Promise<string> => {
  const validationErrors = validateAll(skillId);
  if (validationErrors) {
    return validationErrors;
  }
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  };
  const url = workspaceContentEndpoint(
    skillId,
    useNewIAUpdatedAccess,
    assistantId
  );
  const formData = new FormData();
  formData.append('intent-csv', file);
  try {
    const response = await axios.post(url, formData, config);
    return _.get(response, 'headers.location', null);
  } catch (error) {
    throw _.get(error, 'response') || error;
  }
};

export const getImportIntentsJobInfo = async (
  location: string
): Promise<{
  finished: boolean;
  data?: number;
}> => {
  const response = await axios.get(endpoints.store + location);
  return response.data;
};

export const exportIntents = async (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  intents: Intent[] = [],
  invert: boolean,
  selectAll: boolean,
  additionalOptions = { name: '' }
): Promise<void> => {
  const { name } = additionalOptions;
  const url = exportIntentsEndpoint(
    skillId,
    useNewIAUpdatedAccess,
    assistantId
  );
  const data = {
    all: selectAll,
    invert,
    name: 'intent',
    names: intents.map(intent => intent.intent)
  };
  const response = await axios.post<Intent[]>(url, data);
  const result = getFormatter(response.data, 'intents');
  const filename = _.isEmpty(name)
    ? `${skillId}_intents.csv`
    : `${skillId}_${name}.csv`;
  const file = new Blob([result], { type: 'text/csv; charset=UTF-8' });
  if (navigator.msSaveBlob) {
    navigator.msSaveBlob(file, filename);
  } else {
    saveAs(URL.createObjectURL(file), filename);
  }
};
